import React, { useReducer } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './Widget.css';
import {
    BotState,
    GroupMember,
    Question,
    WhenQuestion,
    ActivitiesQuestion,
    GroupSizeQuestion,
    WhoQuestion,
    WhoIsDoingQuestion,
    SkillsQuestion,
    ActivityTypeQuestion,
    ConfirmQuestion,
    WidgetBookingRequestV1,
    SentQuestion,
    WidgetBookingResponseV1,
} from '../OutdrTypes';
import _ from 'lodash';
import { BookingConversationComponent, BookingNowButton } from './Book';
import { WidgetAction } from '../actions/WidgetActions';
import { i18n } from '../i18n';
import moment from 'moment';


const when = (location: string, activities: string[]): Question => (
    {
        qn: 0,
        type: 'when',
        editing: true,
        show: true,
        skip: false,
        start: null,
        end: null,
        nDays: 0,
        nextDisabled: true,
        location: location,
        activities
    }
);

const activities = (activities: string[]): Question => {
    return {
        qn: 0,
        type: 'activities',
        editing: true,
        show: false,
        skip: false,
        days: _.fromPairs(activities.map(a => [a, 0])),
        maxDays: 0,
        nextDisabled: true
    }
};

const groupSize = (maxGroupSize: number): Question => (
    {
        qn: 0,
        type: 'group-size',
        editing: true,
        show: false,
        skip: false,
        groupSize: 0,
        maxGroupSize
    }
);

const who = (user: { name: string, email: string } | null, maxGroupSize: number): Question => {

    const createGroupMember = (i: number): GroupMember => {
        return {
            index: i,
            name: '',
            email: '',
            tel: '',
            editable: true,
            emailValid: i !== 0,
            nameValid: i !== 0,
            required: i === 0,
            requestTel: i === 0
        };
    };

    const emptyGroup = _.range(0, maxGroupSize).map(createGroupMember);

    const user0 = user ? { ...emptyGroup[0], ...user, editable: false, emailValid: true, nameValid: true } : emptyGroup[0];

    const group = [user0, ...emptyGroup.slice(1)]

    return {
        qn: 0,
        type: 'who',
        editing: true,
        show: false,
        skip: false,
        group: group,
        groupSize: 0,
        nextDisabled: true
    };
};

const whoIsDoing = (activities: string[], maxGroupSize: number): Question[] => {
    return activities.map(a => {
        return {
            qn: 0,
            type: 'who-is-doing',
            activity: a,
            editing: true,
            show: false,
            skip: false,
            groupSize: 0,
            groupNames: _.times(maxGroupSize, _.constant('')),
            selected: _.times(maxGroupSize, _.constant(false)),
            nextDisabled: true
        } as WhoIsDoingQuestion;
    });
};

const skills = (activities: string[], maxGroupSize: number): Question[] => {
    return _.flatMap(activities, a =>
        _.range(0, maxGroupSize).map(i => {
            return {
                qn: 0,
                type: 'skill',
                activity: a,
                editing: true,
                show: false,
                skip: false,
                groupSize: 0,
                index: i,
                name: '',
                skill: ''
            } as SkillsQuestion;
        })
    )
};

const activityTypes = (activities: string[]): Question[] => {
    return activities.map(a => {
        return {
            qn: 0,
            type: 'activity-type',
            activity: a,
            editing: true,
            show: false,
            skip: false,
            activityType: ''
        } as ActivityTypeQuestion;
    });
};

const confirm = (apiDomain: string, fakeApi: boolean): ConfirmQuestion => {
    return {
        type: 'confirm',
        qn: 0,
        show: false,
        skip: false,
        editing: true,
        request: null,
        apiDomain,
        fakeApi
    };
};

const sent = (): SentQuestion => {
    return {
        type: 'sent',
        qn: 0,
        show: false,
        skip: false,
        editing: true,
        response: null
    }
};

const createInitialState = (props: WidgetProps): BotState => {
    const { activities: allActivities, locationName, maxGroupSize, user } = props;

    return {
        parameters: props,
        expanded: false,
        sent: false,
        questions: [
            when(locationName, allActivities),
            activities(allActivities),
            groupSize(maxGroupSize),
            who(user, maxGroupSize),
            ...whoIsDoing(allActivities, maxGroupSize),
            ...skills(allActivities, maxGroupSize),
            ...activityTypes(allActivities),
            confirm(props.apiDomain, props.fakeApi),
            sent()
        ].map((q, i) => ({ ...q, qn: i })),
        language: i18n.language,
        request: null
    };
};

const setFieldsWhere = (botState: BotState, fieldName: string, value: any, { type, index, activity }: { type?: string, index?: number, activity?: string }): BotState => {
    const questions = botState.questions.map(q =>
        (type === undefined || type === q.type)
            && (index === undefined || index === q.index)
            && (activity === undefined || activity === q.activity)
            && ((q as any)[fieldName] !== undefined)
            ? { ...q, [fieldName]: value }
            : q
    );

    return { ...botState, questions }
};

const updateQuestion = (botState: BotState, question: Question) => {
    const questions = botState.questions.slice();
    questions[question.qn] = question;
    return { ...botState, questions };
};

const whenSetDates = (botState: BotState, { startDate, endDate }: { startDate: moment.Moment | null, endDate: moment.Moment | null }): BotState => {
    const when = findQuestion(botState, { type: 'when' }) as WhenQuestion;

    const start = startDate || when.start;
    const end = endDate || when.end || start;
    const nDays = (start && end && ((end.diff(start, 'days')) + 1)) || 1;

    const newState = updateQuestion(botState, {
        ...when,
        start,
        end,
        nextDisabled: false,
        nDays
    });

    const activities = findQuestion(newState, { type: 'activities' }) as ActivitiesQuestion;

    const defaultDays = _.keys(activities.days).length === 1 ? nDays : 0;
    const days = activities.editing ? _.mapValues(activities.days, () => defaultDays) : activities.days;
    return updateQuestion(newState, {
        ...activities,
        maxDays: nDays,
        days,
        nextDisabled: _.every(_.values(days), d => d <= 0)
    });
};

const nextQuestion = (botState: BotState) => {
    const currentIndex = _.findLastIndex(botState.questions, q => q.show);
    const nextEditing = _.find(botState.questions, q => q.editing && !q.show && !q.skip);

    if (nextEditing === undefined) {
        console.error("couldn't find next question")
        return botState;
    }

    const newBotState = (nextEditing.type === 'confirm') ? buildRequest(botState) : botState;

    const questions = newBotState.questions.map((q, i) => {
        if (i === currentIndex) {
            return { ...q, editing: false };
        }
        if (i > currentIndex && i <= nextEditing.qn) {
            return { ...q, show: true };
        }
        return q;
    });

    return {
        ...newBotState,
        questions
    }
};

const editAnswer = (state: BotState, { qn }: { qn: number }) => {
    if (state.sent) {
        return state;
    }

    const questions = state.questions.map((q) => {
        if (q.qn === qn) {
            return { ...q, editing: true };
        }
        if (q.qn > qn) {
            return { ...q, show: false };
        }
        return q;
    });
    return { ...state, questions };
};

const activityClicked = (botState: BotState, { activity }: { activity: string }) => {
    const activities = findQuestion(botState, { type: 'activities' }) as ActivitiesQuestion;

    const days = { ...activities.days, [activity]: activities.days[activity] > 0 ? 0 : 1 };
    const nextDisabled = _.every(_.values(days), d => d <= 0);

    return updateQuestion(botState, { ...activities, days, nextDisabled });
};

const activityDaysChanged = (botState: BotState, { activity, delta }: { activity: string, delta: number }) => {
    const activities = findQuestion(botState, { type: 'activities' }) as ActivitiesQuestion;

    const days = { ...activities.days, [activity]: _.clamp(activities.days[activity] + delta, 0, activities.maxDays) };
    const nextDisabled = _.every(_.values(days), d => d <= 0);

    return updateQuestion(botState, { ...activities, days, nextDisabled });
};

const activityNext = (botState: BotState) => {
    const activities = findQuestion(botState, { type: 'activities' }) as ActivitiesQuestion;
    const { groupSize } = findQuestion(botState, { type: 'group-size' }) as GroupSizeQuestion;

    let s = botState;
    _.forEach(activities.days, (d, activity) => {
        s = {
            ...s, questions: s.questions.map(q => {
                if (q.activity === activity) {
                    return { ...q, skip: d <= 0 || (q.index !== undefined && q.index >= groupSize) }
                }
                return q;
            })
        };
    });

    let numberOfSelectedActivities = _.sumBy(_.values(activities.days), a => a > 0 ? 1 : 0);
    if (numberOfSelectedActivities < 2) {
        s = setFieldsWhere(s, 'skip', true, { type: 'who-is-doing' });
    }

    return nextQuestion(s);
};

const selectGroupSize = (botState: BotState, { size }: { size: number }) => {
    const groupSize = findQuestion(botState, { type: 'group-size' }) as GroupSizeQuestion;
    const activities = findQuestion(botState, { type: 'activities' }) as ActivitiesQuestion;
    const changed = size !== groupSize.groupSize;
    let newState = updateQuestion(botState, { ...groupSize, editing: false, groupSize: size });
    newState = setFieldsWhere(newState, 'groupSize', size, {});

    for (let n = 0; n < groupSize.maxGroupSize; n++) {
        _.forEach(activities.days, (days, activity) => {
            newState = setFieldsWhere(newState, 'skip', n >= size || days < 1, { index: n, activity });
        });
    }

    if (changed) {
        newState = setFieldsWhere(newState, 'editing', true, { type: 'who' });
        newState = setFieldsWhere(newState, 'editing', true, { type: 'who-is-doing' });
    }

    newState = nextQuestion(newState);
    return newState;
};

const updateGroupMember = (state: BotState, index: number, update: (gm: GroupMember) => GroupMember) => {
    const who = findQuestion(state, { type: 'who' }) as WhoQuestion;
    const group = who.group.slice();
    group[index] = update(who.group[index]);
    const nextDisabled = !_.every(group, m => m.emailValid && m.nameValid);
    const newWho = { ...who, nextDisabled, group };
    return updateQuestion(state, newWho);
};

const whoUpdateNameField = (state: BotState, { index, name }: { index: number, name: string }): BotState => {
    const couldBeEmailAddress = name.indexOf('@') > 0;
    if (couldBeEmailAddress) {
        return { ... state };
    }

    const nameValid = !!name && name.trim().length > 2;
    return updateGroupMember(state, index, m => ({ ...m, name, nameValid }));
};

const validEmailRegex = /^[^@\n]+@([\w-]+\.)+[\w-]{2,}$/;

const whoUpdateEmailField = (state: BotState, { index, email }: { index: number, email: string }): BotState => {
    const { group } = findQuestion(state, { type: 'who' }) as WhoQuestion;
    const emailValid = (!email && !group[index].required) || validEmailRegex.test(email);
    return updateGroupMember(state, index, m => ({ ...m, email, emailValid }));
};

const whoUpdateTelField = (state: BotState, { index, tel }: { index: number, tel: string }): BotState => {
    return updateGroupMember(state, index, m => ({ ...m, tel }));
};

const whoNext = (state: BotState): BotState => {
    const whoActivity = findQuestion(state, { type: 'who' }) as WhoQuestion;
    const groupNames = whoActivity.group.map((m, i) => m.name || `Guest ${i}`);
    let newState = setFieldsWhere(state, 'groupNames', groupNames, {});

    for (let n = 0; n < whoActivity.groupSize; n++) {
        newState = setFieldsWhere(newState, 'name', groupNames[n], { index: n });
    }

    return nextQuestion(newState);
};

const toggleWhoActivity = (state: BotState, { index, activity }: { index: number, activity: string }): BotState => {
    const whoIsDoing = findQuestion(state, { type: 'who-is-doing', activity }) as WhoIsDoingQuestion;
    const selected = whoIsDoing.selected.slice();
    selected[index] = !selected[index];
    const nextDisabled = _.every(selected, m => m === false);
    return updateQuestion(state, { ...whoIsDoing, selected, nextDisabled });
};

const whoActivityNext = (state: BotState, { activity }: { activity: string }): BotState => {
    const whoActivity = findQuestion(state, { type: 'who-is-doing', activity }) as WhoIsDoingQuestion;

    let newState = state;
    _.forEach(whoActivity.selected, (selected, index) => {
        newState = setFieldsWhere(newState, 'skip', !selected, { activity: whoActivity.activity, index });
    });
    return nextQuestion(newState);
};

const skillsSelect = (state: BotState, { activity, index, skill }: { activity: string, index: number, skill: string }) => {
    const skillsQuestion = findQuestion(state, { type: 'skill', activity, index }) as SkillsQuestion;
    let newState = updateQuestion(state, { ...skillsQuestion, skill });

    if (skill === 'first time' || skill === 'beginner') {
        newState = setFieldsWhere(newState, 'activityType', 'lessons', { type: 'activity-type', activity });
        newState = setFieldsWhere(newState, 'skip', true, { type: 'activity-type', activity })
    }
    return nextQuestion(newState);
};

const typeActivitySelect = (state: BotState, { activity, activityType }: { activity: string, activityType: string }) => {
    const q = findQuestion(state, { type: 'activity-type', activity }) as ActivityTypeQuestion;
    return nextQuestion(updateQuestion(state, { ...q, activityType, editing: false }));
};

const findQuestion = (botState: BotState, { type, activity, index }: { type: string, activity?: string, index?: number }): Question | undefined => {
    return _.find(botState.questions, q => {
        return (q.type === type || type === undefined)
            && (q.activity === activity || activity === undefined)
            && (q.index === index || index === undefined)
    });
};

const buildRequest = (botState: BotState): BotState => {

    const whenQuestion = findQuestion(botState, { type: 'when' }) as WhenQuestion;
    const whoQuestion = findQuestion(botState, { type: 'who' }) as WhoQuestion;
    const confirmQuestion = findQuestion(botState, { type: 'confirm' }) as ConfirmQuestion;
    const activitiesQuestion = findQuestion(botState, { type: 'activities' }) as ActivitiesQuestion;

    if (!whenQuestion.start || !whenQuestion.end) {
        throw new Error("assertion failed, date range no set correctly");
    }

    const group =
        whoQuestion.group
            .filter((gm, i) => gm.index < whoQuestion.groupSize)
            .map(gm => {

                const activitySkillsPairs =
                    _.filter(botState.questions, q => q.type === 'skill' && q.index === gm.index)
                        .map(q => q as SkillsQuestion)
                        .filter(q => !q.skip)
                        .map(q => [q.activity, q.skill])

                return {
                    name: gm.name,
                    email: gm.email,
                    tel: gm.tel,
                    skills: _.fromPairs(activitySkillsPairs)
                }
            });

    const activities =
        _.toPairs(activitiesQuestion.days)
            .filter(([_, days]) => days > 0)
            .map(([activity, days]) => {
                const typeQuestion = findQuestion(botState, { type: 'activity-type', activity }) as ActivityTypeQuestion;
                return { activity, days, sessionType: typeQuestion.activityType };
            })

    const primaryMember = whoQuestion.group[0];

    const request: WidgetBookingRequestV1 = {
        schoolId: botState.parameters.schoolId,
        start: whenQuestion.start,
        end: whenQuestion.end,
        name: primaryMember.name,
        email: primaryMember.email,
        tel: primaryMember.tel,
        language: botState.language || 'en',
        group: group,
        activities
    };

    const newConfirmQuestion = { ...confirmQuestion, request };

    return updateQuestion({ ...botState, request }, newConfirmQuestion);
};


const handleResponse = (state: BotState, { json }: { json: string  }) => {
    const response = JSON.parse(json) as WidgetBookingResponseV1;
    const sentQuestion = findQuestion(state, { type: 'sent' }) as SentQuestion;

    return updateQuestion(state, { ...sentQuestion, response });
};

const reduce = (state: BotState, action: WidgetAction): BotState => {

    console.log("action", action);

    const apply = () => {
        switch (action.type) {
            case 'when-set-dates':
                return whenSetDates(state, action);
            case 'when-next':
                return nextQuestion(state);
            case 'activity-clicked':
                return activityClicked(state, action);
            case 'activity-days-changed':
                return activityDaysChanged(state, action);
            case 'activity-next':
                return activityNext(state);
            case 'groupsize-select':
                return selectGroupSize(state, action);
            case 'who-update-name':
                return whoUpdateNameField(state, action);
            case 'who-update-email':
                return whoUpdateEmailField(state, action);
            case 'who-update-tel':
                return whoUpdateTelField(state, action);
            case 'who-next':
                return whoNext(state);
            case 'who-activity-toggle':
                return toggleWhoActivity(state, action);
            case 'who-activity-next':
                return whoActivityNext(state, action);
            case 'skills-select':
                return skillsSelect(state, action);
            case 'type-activity-select':
                return typeActivitySelect(state, action);
            case 'edit':
                return editAnswer(state, action);
            case 'book-now-clicked':
                return { ...state, expanded: true };
            case 'close-clicked':
            case 'reset':
                return createInitialState(state.parameters);
            case 'send':
                return nextQuestion({ ...state, sent: true });
            case 'response':
                return handleResponse(state, action);
            default:
                return state;
        }
    }

    const newState = apply();

    console.log("newState", newState);

    return newState;
};

export type ButtonPosition = 
    | 'inline' 
    | 'top-left' 
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    ;

export interface WidgetProps {
    stylesheetPath: string;
    activities: string[];
    maxGroupSize: number;
    locationName: string;
    user: { name: string, email: string } | null;
    schoolId: string;
    apiDomain: string;
    fakeApi: boolean;
    highlightColour: string;
    customCss: string;
    buttonPosition: ButtonPosition;
}

const Widget = (props: WidgetProps) => {

    const [state, dispatch] = useReducer(reduce, props, createInitialState)

    return (
        <>
        {state.expanded &&
             <BookingConversationComponent
                sent={state.sent}
                questions={state.questions}
                dispatch={dispatch}
            />
        }
        {(!state.expanded || props.buttonPosition === 'inline') &&
            <BookingNowButton dispatch={dispatch} position={props.buttonPosition} />
        }
        </>
    );
};

export default Widget;
